"use client";

import React, { useEffect, useState, ReactNode } from "react";

import DemoModeBanner from "@/components/ui/demo-mode-banner";
import { DemoStorage } from "@/lib/demo-mode/client";
import { EmailPreviewProvider } from "@/lib/demo-mode/email-preview-modal";
import { useDemoNotificationQueue } from "@/lib/demo-mode/notification-queue";
import { NotificationProvider } from "@/lib/demo-mode/notifications";

/**
 * Inner component that watches for server-queued notifications.
 * Must be inside NotificationProvider and EmailPreviewProvider to access contexts.
 */
function DemoNotificationWatcher({ children }: { children: ReactNode }) {
  useDemoNotificationQueue();
  return <>{children}</>;
}

export default function DemoProviders({ children }: { children: ReactNode }) {
  const [isDemoMode, setIsDemoMode] = useState(false);

  useEffect(() => {
    // Check if demo mode is active using the same logic as demo banner
    const checkDemoMode = () => {
      const demoConfig = DemoStorage.getConfig();
      setIsDemoMode(!!demoConfig?.enabled);
    };

    checkDemoMode();

    // Check periodically in case demo mode is activated/deactivated
    const interval = setInterval(checkDemoMode, 5000);
    return () => clearInterval(interval);
  }, []);

  if (!isDemoMode) {
    return <>{children}</>;
  }

  return (
    <EmailPreviewProvider>
      <NotificationProvider>
        <DemoNotificationWatcher>
          {children}
          <DemoModeBanner />
        </DemoNotificationWatcher>
      </NotificationProvider>
    </EmailPreviewProvider>
  );
}
